// V8 landing page: one design sold as several products (cap, tee, sweatshirt, jacket).
// The page has three buy areas (data-look a | b | c, see sections/om-v8-landing.liquid);
// they share the same product groups, price maths and cart call.

// One product's color / size controls somewhere on the page.
class OmV8Group {
  constructor(element, products) {
    this.element = element;
    this.key = element.dataset.item;
    this.name = products[this.key].name;
    this.variants = products[this.key].variants;
  }

  // Chosen value per option position; '' while a size is not chosen yet.
  values() {
    const values = [];
    this.element.querySelectorAll('[data-option]').forEach((field) => {
      if (field.type === 'radio' && !field.checked) return;
      values[Number(field.dataset.option)] = field.value;
    });
    return values;
  }

  matching() {
    const values = this.values();
    return this.variants.filter((variant) => variant.available && variant.options.every((option, i) => !values[i] || option === values[i]));
  }

  variant() {
    const values = this.values();
    if (values.includes('')) return null;
    return this.variants.find((variant) => variant.options.every((option, i) => option === values[i])) || null;
  }

  // Price to count: the chosen variant, or the cheapest one still possible.
  price() {
    const variant = this.variant();
    if (variant) return variant.price;
    const matching = this.matching();
    return matching.length ? Math.min(...matching.map((entry) => entry.price)) : 0;
  }

  // True while the choices still left open could change the price.
  priceVaries() {
    return new Set(this.matching().map((entry) => entry.price)).size > 1;
  }

  refresh(money) {
    const values = this.values();
    const variant = this.variant();

    // Values no available variant can satisfy with the other current choices are disabled.
    this.element.querySelectorAll('[data-option]').forEach((field) => {
      const index = Number(field.dataset.option);
      const reachable = (value) =>
        this.variants.some(
          (entry) =>
            entry.available && entry.options[index] === value && entry.options.every((o, i) => i === index || !values[i] || o === values[i])
        );
      if (field.type === 'radio') field.disabled = !reachable(field.value);
      else [...field.options].forEach((option) => (option.disabled = !!option.value && !reachable(option.value)));
    });

    this.element.querySelectorAll('[data-current]').forEach((label) => {
      label.textContent = values[Number(label.dataset.current)] || '';
    });

    const price = this.element.querySelector('[data-price]');
    if (price) {
      price.textContent = money(this.price());
      // The crossed-out price shows as soon as the price itself is settled.
      const priced = variant || (this.priceVaries() ? null : this.matching()[0]);
      if (priced && priced.compare > priced.price) {
        const was = document.createElement('s');
        was.textContent = money(priced.compare);
        price.append(' ', was);
      }
    }

    const image = (variant || this.matching()[0])?.image;
    const thumb = this.element.querySelector('[data-thumb]');
    if (image && thumb && !thumb.src.endsWith(image)) thumb.src = image;
  }
}

if (!customElements.get('om-v8-landing')) {
  customElements.define(
    'om-v8-landing',
    class OmV8Landing extends HTMLElement {
      connectedCallback() {
        this.look = this.dataset.look;
        this.products = JSON.parse(this.querySelector('script[data-om-v8-products]').textContent);
        this.freeCount = Number(this.dataset.freeCount);
        this.discountCount = Number(this.dataset.discountCount);
        this.discountPercent = Number(this.dataset.discountPercent);
        this.busy = false;

        this.form = this.querySelector('[data-buy]');
        this.button = this.form.querySelector('[data-submit]');
        this.status = this.form.querySelector('[data-status]');
        this.groups = this.groupsIn(this.form);

        this.form.addEventListener('change', (event) => {
          this.status.textContent = '';
          this.update();
          // A tab picked while half outside the scrolling row moves fully into view.
          if (event.target.matches('[data-tab]')) {
            const tab = event.target.closest('label');
            const row = tab.parentElement;
            const edge = 16;
            const over = tab.getBoundingClientRect().right - (row.getBoundingClientRect().right - edge);
            const under = row.getBoundingClientRect().left + edge - tab.getBoundingClientRect().left;
            if (over > 0) row.scrollLeft += over;
            else if (under > 0) row.scrollLeft -= under;
          }
        });
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.submit();
        });
        this.addEventListener('click', (event) => this.onClick(event));

        this.mixCrossSell();
        this.setUpHero();
        this.upsell = this.querySelector('[data-upsell]');
        if (this.upsell) this.setUpUpsell();
        this.setUpViewers();
        this.setUpHeroZoom();
        this.setUpBar();
        this.setUpConfess();
        this.setUpSpin();
        this.setUpWho();
        this.setUpParts();
        this.setUpFormats();
        this.setUpStack();
        this.setUpPark();
        this.setUpMiles();
        this.setUpPour();
        this.setUpBuild();
        this.setUpLouder();
        this.setUpHuhs();
        this.setUpNeedle();
        this.setUpGroove();
        this.setUpQuick();
        this.preselect();
        this.update();
      }

      // An ad can link straight to what it showed: ?item=jacket picks that item's tab,
      // and ?with=cap,tee ticks those items in the popup after Add to cart.
      preselect() {
        const params = new URLSearchParams(window.location.search);
        const tab = this.form.querySelector('[data-tab][value="' + (params.get('item') || '').toLowerCase() + '"]');
        if (tab) {
          tab.checked = true;
          // Bring a tab that starts outside the scrolling row into view.
          const label = tab.closest('label');
          const row = label.parentElement;
          const over = label.getBoundingClientRect().right - (row.getBoundingClientRect().right - 16);
          if (over > 0) row.scrollLeft += over;
        }
        this.preticked = (params.get('with') || '').toLowerCase().split(',').map((key) => key.trim()).filter(Boolean);
      }

      // "Which ones did you do": tick the lines, the score line answers.
      // Score lines read "up to N | message"; the first one that covers the count wins.
      setUpConfess() {
        const block = this.querySelector('[data-confess]');
        if (!block) return;
        const items = [...block.querySelectorAll('[data-confess-item]')];
        const scores = JSON.parse(block.dataset.scores || '[]')
          .map((line) => line.split('|'))
          .filter((parts) => parts.length > 1)
          .map((parts) => ({ max: Number(parts[0]), text: parts.slice(1).join('|').trim() }));
        const count = block.querySelector('[data-confess-count]');
        const line = block.querySelector('[data-confess-line]');
        const show = () => {
          const ticked = items.filter((item) => item.checked).length;
          this.showPicks(block, ticked);
          count.textContent = ticked + ' of ' + items.length;
          line.textContent = (scores.find((score) => ticked <= score.max) || scores[scores.length - 1] || {}).text || '';
          // Look T: the record beside the heading starts and speeds up as lines are ticked.
          const record = block.querySelector('[data-turntable]');
          if (record) {
            const share = items.length ? ticked / items.length : 0;
            record.dataset.rpm = share === 0 ? '0' : share <= 1 / 3 ? '33' : share <= 2 / 3 ? '45' : '78';
          }
        };
        block.addEventListener('change', show);
        show();
      }

      // Look T: the record in the header spins until the visitor lifts the needle.
      setUpSpin() {
        const toggle = this.querySelector('[data-spin-toggle]');
        const record = this.querySelector('[data-turntable="hero"]');
        if (!toggle || !record) return;
        toggle.addEventListener('click', () => {
          const stopped = record.dataset.rpm === '0';
          record.dataset.rpm = stopped ? '33' : '0';
          toggle.setAttribute('aria-pressed', String(!stopped));
          toggle.querySelector('[data-spin-label]').textContent = stopped ? 'Lift the needle' : 'Drop the needle';
          toggle.querySelector('[data-spin-icon]').setAttribute('d', stopped ? 'M6 5h4v14H6zM14 5h4v14h-4z' : 'M7 4l13 8-13 8z');
        });
      }

      // Self or gift buyer: the switch swaps every [data-who-text] line on the page
      // and turns the top photos to the gift photo (or back to the first one).
      setUpWho() {
        const group = this.querySelector('[data-who-group]');
        if (!group) return;
        group.addEventListener('click', (event) => {
          const button = event.target.closest('[data-who]');
          if (!button) return;
          const who = button.dataset.who;
          group.querySelectorAll('[data-who]').forEach((other) => other.setAttribute('aria-pressed', String(other === button)));
          this.querySelectorAll('[data-who-text]').forEach((line) => {
            line.hidden = line.dataset.whoText !== who;
          });
          const slide = who === 'gift' ? Number(group.dataset.giftSlide) || 0 : 0;
          this.querySelector('[data-hero-thumb="' + slide + '"]')?.click();
        });
      }

      // The piece an interactive block suggests (snippet om-v8-picks): nothing before the first tap,
      // then the first card whose "up to N" covers the count (the last one past every limit).
      showPicks(block, count) {
        const offers = [...block.querySelectorAll('[data-pick-offer]')];
        const offer = count ? offers.find((entry) => count <= Number(entry.dataset.pickOffer)) || offers[offers.length - 1] : null;
        offers.forEach((entry) => (entry.hidden = entry !== offer));
      }

      // Score lines "up to N | title | line" from a block's data-scores; the first that covers the count wins.
      scoreFor(block, count) {
        const scores = JSON.parse(block.dataset.scores || '[]')
          .map((line) => line.split('|').map((part) => part.trim()))
          .filter((cells) => cells.length > 2)
          .map((cells) => ({ max: Number(cells[0]), title: cells[1].replace('{n}', count), line: cells[2] }));
        return scores.find((entry) => count <= entry.max) || scores[scores.length - 1];
      }

      // "Which parts are aftermarket?": each tapped label gets a stamp; the score line follows the count.
      setUpParts() {
        const block = this.querySelector('[data-parts]');
        if (!block) return;
        const parts = [...block.querySelectorAll('[data-part]')];
        const show = () => {
          const tapped = parts.filter((part) => part.getAttribute('aria-pressed') === 'true').length;
          this.showPicks(block, tapped);
          const score = this.scoreFor(block, tapped);
          if (!score) return;
          block.querySelector('[data-parts-title]').textContent = score.title;
          block.querySelector('[data-parts-line]').textContent = score.line;
        };
        block.addEventListener('click', (event) => {
          const part = event.target.closest('[data-part]');
          if (!part) return;
          part.setAttribute('aria-pressed', String(part.getAttribute('aria-pressed') !== 'true'));
          show();
        });
        show();
      }

      // Restart a CSS animation on an element by taking its trigger class off and back on.
      replay(element, className) {
        element.classList.remove(className);
        void element.offsetWidth;
        element.classList.add(className);
      }

      // Looks S and X: the formats in the header play once on load (CSS); a tap plays one again.
      setUpFormats() {
        this.querySelectorAll('[data-format-play]').forEach((button) => {
          button.addEventListener('click', () => this.replay(button, 'is-replay'));
        });
      }

      // Look S, "Build your stack": a tapped format drops onto the top of the shelf, a second
      // tap takes it off; the score line follows the count.
      setUpStack() {
        const block = this.querySelector('[data-stack]');
        if (!block) return;
        const shelf = block.querySelector('[data-shelf]');
        const picks = [...block.querySelectorAll('[data-stack-pick]')];
        const show = () => {
          const stacked = picks.filter((pick) => pick.getAttribute('aria-pressed') === 'true').length;
          this.showPicks(block, stacked);
          const score = this.scoreFor(block, stacked);
          if (!score) return;
          block.querySelector('[data-stack-title]').textContent = score.title;
          block.querySelector('[data-stack-line]').textContent = score.line;
        };
        block.addEventListener('click', (event) => {
          const pick = event.target.closest('[data-stack-pick]');
          if (!pick) return;
          const on = pick.getAttribute('aria-pressed') !== 'true';
          pick.setAttribute('aria-pressed', String(on));
          const item = shelf.querySelector('[data-shelf-item="' + pick.dataset.stackPick + '"]');
          item.hidden = !on;
          if (on) {
            // The shelf stacks bottom-up, so the last child is the top of the pile.
            shelf.append(item);
            this.replay(item, 'is-dropped');
          }
          show();
        });
        show();
      }

      // Look M: point a ribbon speedometer at a speed (0-120 MPH) and show an odometer reading.
      setSpeedo(box, mph, reading) {
        const speed = Math.max(0, Math.min(120, mph));
        box.querySelector('[data-speedo-needle]').style.transform = 'translateX(' + speed * 2.5 + 'px)';
        box.querySelector('[data-speedo-ribbon]').style.transform = 'scaleX(' + (speed / 120).toFixed(3) + ')';
        const digits = String(Math.max(0, Math.round(reading))).padStart(6, '0').slice(-6);
        box.querySelectorAll('[data-speedo-drum]').forEach((drum, index) => {
          drum.style.transform = 'translateY(' + Number(digits[index]) * -26 + 'px)';
        });
      }

      // Look M: "Park it" stops the header speedometer and sound wave; a second tap starts them again.
      setUpPark() {
        const button = this.querySelector('[data-park]');
        if (!button) return;
        button.addEventListener('click', () => {
          const parked = this.classList.toggle('is-parked');
          button.setAttribute('aria-pressed', String(parked));
          button.textContent = parked ? 'Start the engine' : 'Park it';
        });
      }

      // Look M, "Add up your miles": each tapped memory adds its miles; the needle moves right with the count.
      setUpMiles() {
        const block = this.querySelector('[data-miles]');
        if (!block) return;
        const picks = [...block.querySelectorAll('[data-mile]')];
        const gauge = block.querySelector('[data-speedo]');
        const show = () => {
          const on = picks.filter((pick) => pick.getAttribute('aria-pressed') === 'true');
          const total = on.reduce((sum, pick) => sum + Number(pick.dataset.mile), 0);
          this.setSpeedo(gauge, picks.length ? (on.length / picks.length) * 120 : 0, total);
          this.showPicks(block, on.length);
          const score = this.scoreFor(block, on.length);
          if (!score) return;
          block.querySelector('[data-miles-title]').textContent = score.title.replace('{miles}', total.toLocaleString('en-US'));
          block.querySelector('[data-miles-line]').textContent = score.line;
        };
        block.addEventListener('click', (event) => {
          const pick = event.target.closest('[data-mile]');
          if (!pick) return;
          pick.setAttribute('aria-pressed', String(pick.getAttribute('aria-pressed') !== 'true'));
          show();
        });
        show();
      }

      // Look K: "Turn it up" steps the header volume 1 > 2 > 3 and back to 1.
      setUpLouder() {
        const button = this.querySelector('[data-louder]');
        if (!button) return;
        const box = this.querySelector('[data-volume]');
        button.addEventListener('click', () => {
          const volume = (Number(box.dataset.volume) % 3) + 1;
          box.dataset.volume = String(volume);
          button.textContent = volume === 3 ? 'Too loud. Reset' : 'Turn it up';
        });
      }

      // Look K, "How many huhs?": each tapped place counts; the score line follows the count.
      setUpHuhs() {
        const block = this.querySelector('[data-huhs]');
        if (!block) return;
        const picks = [...block.querySelectorAll('[data-huh]')];
        const show = () => {
          const said = picks.filter((pick) => pick.getAttribute('aria-pressed') === 'true').length;
          this.showPicks(block, said);
          const score = this.scoreFor(block, said);
          if (!score) return;
          block.querySelector('[data-huh-title]').textContent = score.title;
          block.querySelector('[data-huh-line]').textContent = score.line;
        };
        block.addEventListener('click', (event) => {
          const pick = event.target.closest('[data-huh]');
          if (!pick) return;
          pick.setAttribute('aria-pressed', String(pick.getAttribute('aria-pressed') !== 'true'));
          show();
        });
        show();
      }

      // Look K: the popup's volume bars light up two per piece; Quiet / Normal / LOUD.
      updateVolume(count) {
        const box = this.upsell.querySelector('[data-sheet-volume]');
        if (!box) return;
        const step = Math.min(count, 3);
        box.querySelectorAll('[data-volume-bar]').forEach((bar) => bar.classList.toggle('is-on', Number(bar.dataset.volumeBar) <= step * 2));
        box.querySelector('[data-volume-name]').textContent = ['', 'Quiet', 'Normal', 'LOUD'][step];
        box.querySelector('[data-volume-deal]').textContent =
          count >= this.discountCount ? this.discountPercent + '% off + free shipping.' : count >= this.freeCount ? 'Free shipping. One more for ' + this.discountPercent + '% off.' : 'One more piece: free shipping.';
      }

      // Look X: the popup lights up one format per piece in the cart; all four is "Raised on all of them!".
      updateRack(count) {
        const box = this.upsell.querySelector('[data-sheet-rack]');
        if (!box) return;
        const lit = Math.min(count, 4);
        box.querySelectorAll('[data-rack-icon]').forEach((icon, i) => icon.classList.toggle('is-on', i < lit));
        box.querySelector('[data-rack-name]').textContent = lit === 4 ? 'Raised on all of them!' : lit + ' of 4 on the shelf';
        box.querySelector('[data-rack-deal]').textContent =
          count >= this.discountCount ? this.discountPercent + '% off + free shipping.' : count >= this.freeCount ? 'Free shipping. One more for ' + this.discountPercent + '% off.' : 'One more piece: free shipping.';
      }

      // Look N: "Hear the crackle" plays a soft record crackle made on the spot (a hiss with a few
      // clicks, no sound file) and drops the needle again. Sound only ever starts from this tap.
      setUpNeedle() {
        const button = this.querySelector('[data-crackle]');
        if (!button) return;
        const head = this.querySelector('[data-needle]');
        button.addEventListener('click', () => {
          this.replay(head, 'is-dropping');
          try {
            const Context = window.AudioContext || window.webkitAudioContext;
            if (!Context) return;
            const audio = new Context();
            const length = Math.floor(audio.sampleRate * 2.5);
            const buffer = audio.createBuffer(1, length, audio.sampleRate);
            const data = buffer.getChannelData(0);
            for (let i = 0; i < length; i++) {
              data[i] = (Math.random() * 2 - 1) * 0.015;
              if (Math.random() < 0.0009) {
                const click = Math.min(length - i, 60);
                for (let j = 0; j < click; j++) data[i + j] += (Math.random() * 2 - 1) * 0.5 * (1 - j / click);
              }
            }
            const source = audio.createBufferSource();
            const gain = audio.createGain();
            source.buffer = buffer;
            gain.gain.value = 0.35;
            source.connect(gain);
            gain.connect(audio.destination);
            source.onended = () => audio.close();
            source.start();
          } catch (error) {
            // No sound on this device; the needle still drops.
          }
        });
      }

      // Look N, "How deep in the groove?": each tapped memory moves the tonearm one groove in; the
      // score line follows the count and the first suggested piece that covers the count shows.
      // Look D, "Pour him a glass": the same block with a glass, a finger poured per memory.
      setUpGroove() {
        const block = this.querySelector('[data-groove]');
        if (!block) return;
        const picks = [...block.querySelectorAll('[data-groove-pick]')];
        const arm = block.querySelector('[data-groove-arm]');
        const glass = block.querySelector('[data-groove-glass]');
        const show = () => {
          const count = picks.filter((pick) => pick.getAttribute('aria-pressed') === 'true').length;
          block.querySelector('[data-groove-count]').textContent = String(count);
          if (arm) {
            arm.style.setProperty('--turn', 12 - count * 6 + 'deg');
            block.querySelector('[data-groove-label]').setAttribute('aria-label', 'The needle is ' + count + ' grooves in');
          }
          if (glass) {
            this.setGlass(glass, (0.8 * count) / Math.max(picks.length, 1), {});
            glass.querySelector('svg').setAttribute('aria-label', count ? count + (count === 1 ? ' finger' : ' fingers') + ' poured' : 'An empty glass');
          }
          const score = this.scoreFor(block, count);
          if (score) {
            block.querySelector('[data-groove-title]').textContent = score.title;
            block.querySelector('[data-groove-line]').textContent = score.line;
          }
          this.showPicks(block, count);
        };
        block.addEventListener('click', (event) => {
          const pick = event.target.closest('[data-groove-pick]');
          if (!pick) return;
          pick.setAttribute('aria-pressed', String(pick.getAttribute('aria-pressed') !== 'true'));
          show();
        });
        show();
      }

      // Look N: the popup's needle moves one track in per piece; A1 / A2 / A3.
      updateNeedle(count) {
        const box = this.upsell.querySelector('[data-sheet-needle]');
        if (!box) return;
        const step = Math.min(count, 3);
        box.querySelector('[data-needle-arm]').style.setProperty('--turn', (step - 1) * 9 + 'deg');
        box.querySelector('[data-needle-name]').textContent = 'Track A' + step + ' is playing';
        box.querySelector('[data-needle-deal]').textContent =
          count >= this.discountCount ? this.discountPercent + '% off + free shipping.' : count >= this.freeCount ? 'Free shipping. One more for ' + this.discountPercent + '% off.' : 'One more piece: free shipping.';
      }

      // Quick add: a Choose button (or the sticky bar) opens a small popup for one piece with its own
      // color, size and Add to cart; after adding, the matching-pieces popup follows as usual.
      setUpQuick() {
        this.quick = this.querySelector('[data-quick]');
        if (!this.quick) return;
        this.quickForm = this.quick.querySelector('[data-quick-form]');
        this.quickButton = this.quick.querySelector('[data-quick-submit]');
        this.quickStatus = this.quick.querySelector('[data-quick-status]');
        this.quickGroups = this.groupsIn(this.quick);
        this.quickForm.addEventListener('change', () => {
          this.quickStatus.textContent = '';
          this.refreshQuick();
        });
        this.quickForm.addEventListener('submit', async (event) => {
          event.preventDefault();
          if (this.busy || !this.quickGroup) return;
          const group = this.quickGroup;
          const lines = this.lines([group], this.quickStatus);
          if (!lines) return;
          const data = await this.post(lines, this.quickButton, this.quickStatus);
          if (!data) return;
          this.quick.close();
          if (this.upsell && !this.upsellShown && this.openUpsell(group, data)) return;
          this.openCart(data);
        });
        this.quick.querySelector('[data-quick-close]').addEventListener('click', () => this.quick.close());
        this.quick.addEventListener('click', (event) => {
          if (event.target === this.quick && !this.busy) this.quick.close();
        });
      }

      // Opens Quick add on one piece, starting from the color and size picked for it in the buy area
      // (or the color the button names).
      openQuick(key, color) {
        const group = this.quickGroups.find((entry) => entry.key === key);
        if (!group) return false;
        this.quickGroups.forEach((entry) => (entry.element.hidden = entry !== group));
        const main = this.groups.find((entry) => entry.key === key);
        const values = main ? main.values() : [];
        group.element.querySelectorAll('select[data-option]').forEach((field) => {
          const index = Number(field.dataset.option);
          const wanted = index === 0 && color ? color : values[index];
          if (wanted && [...field.options].some((option) => option.value === wanted)) field.value = wanted;
        });
        this.quickGroup = group;
        this.quick.querySelector('[data-quick-title]').textContent = 'Choose your ' + group.name.toLowerCase();
        this.quickStatus.textContent = '';
        this.refreshQuick();
        this.quick.showModal();
        return true;
      }

      refreshQuick() {
        const group = this.quickGroup;
        group.refresh((cents) => this.money(cents));
        // The popup shows the photo large, so it asks for a bigger copy of the variant image.
        const thumb = group.element.querySelector('[data-thumb]');
        if (thumb) thumb.loading = 'eager';
        if (thumb && /width=400/.test(thumb.src)) thumb.src = thumb.src.replace('width=400', 'width=800');
        this.quickButton.textContent = 'Add ' + group.name.toLowerCase() + ' to cart';
      }

      // Look P: fill an Old Fashioned glass to a level (0-1) and show or hide its parts.
      setGlass(box, level, parts) {
        box.querySelector('[data-glass-liquid]').style.transform = 'scaleY(' + level.toFixed(3) + ')';
        Object.entries(parts).forEach(([name, on]) => box.querySelector('[data-glass-' + name + ']')?.classList.toggle('is-in', on));
      }

      // Look P: "Pour another" pours the header glass again.
      setUpPour() {
        const button = this.querySelector('[data-pour-again]');
        if (!button) return;
        const glass = this.querySelector('[data-pour-glass]');
        button.addEventListener('click', () => this.replay(glass, 'is-pouring'));
      }

      // Look P, "Build your Old Fashioned": the ingredients go in in order. Tapping a step puts it in
      // along with every step before it; tapping one already in takes the glass back to just before it.
      setUpBuild() {
        const block = this.querySelector('[data-build]');
        if (!block) return;
        const steps = [...block.querySelectorAll('[data-build-step]')];
        const glass = block.querySelector('[data-build-glass]');
        const title = block.querySelector('[data-build-title]');
        const line = block.querySelector('[data-build-line]');
        const done = block.querySelector('[data-build-done]');
        const show = (count) => {
          block.dataset.step = String(count);
          steps.forEach((step, index) => {
            step.setAttribute('aria-pressed', String(index < count));
            step.classList.toggle('is-next', index === count);
          });
          // Sugar, bitters, whiskey, the cube, the peel: the whiskey is the third step.
          this.setGlass(glass, count >= 3 ? 0.78 : 0, { sugar: count >= 1, bitters: count >= 2, ice: count >= 4, peel: count >= 5 });
          const finished = count === steps.length;
          title.textContent = count === 0 ? title.dataset.empty : finished ? title.dataset.done : steps[count - 1].dataset.name + '.';
          line.textContent = count === 0 ? line.dataset.empty : steps[count - 1].dataset.line;
          if (done) done.hidden = !finished;
          this.showPicks(block, count);
        };
        block.addEventListener('click', (event) => {
          const step = event.target.closest('[data-build-step]');
          if (!step) return;
          const index = Number(step.dataset.buildStep) - 1;
          const count = steps.filter((s) => s.getAttribute('aria-pressed') === 'true').length;
          show(index < count ? index : index + 1);
        });
        show(0);
      }

      // Look P: the popup's glass is a third, two thirds or full for 1, 2 or 3+ pieces.
      // Look D names its steps and levels on the strip (one, two, three fingers).
      updatePour(count) {
        const box = this.upsell.querySelector('[data-sheet-pour]');
        if (!box) return;
        const step = Math.min(count, 3);
        const names = box.dataset.pourNames ? box.dataset.pourNames.split('|') : ['A single', 'A double', 'Top shelf'];
        const levels = box.dataset.pourLevels ? box.dataset.pourLevels.split('|').map(Number) : [0.3, 0.55, 0.8];
        this.setGlass(box, levels[step - 1], { ice: step >= 2, peel: step >= 3 });
        box.querySelector('[data-pour-name]').textContent = names[step - 1];
        box.querySelector('[data-pour-deal]').textContent =
          count >= this.discountCount ? this.discountPercent + '% off + free shipping.' : count >= this.freeCount ? 'Free shipping. One more for ' + this.discountPercent + '% off.' : 'One more piece: free shipping.';
      }

      // Look M: the popup's needle sits at 30, 60 or 90 MPH for 1, 2 or 3+ pieces.
      updateSpeedo(count) {
        const box = this.upsell.querySelector('[data-sheet-speedo]');
        if (!box) return;
        const step = Math.min(count, 3);
        this.setSpeedo(box.querySelector('[data-speedo]'), step * 30, count);
        box.querySelector('[data-speedo-name]').textContent = step * 30 + ' MPH';
        box.querySelector('[data-speedo-deal]').textContent =
          count >= this.discountCount ? this.discountPercent + '% off + free shipping.' : count >= this.freeCount ? 'Free shipping. One more for ' + this.discountPercent + '% off.' : 'Add one more piece and shipping is free.';
      }

      // Look S: the popup stacks the item in the cart and each ticked piece; the deals light up.
      updatePile(ticked) {
        const box = this.upsell.querySelector('[data-sheet-stack]');
        if (!box) return;
        const count = ticked.length + 1;
        const pile = box.querySelector('[data-pile]');
        const before = pile.children.length;
        pile.replaceChildren(...[(this.addedName || 'Your item') + ' (in cart)', ...ticked.map((group) => group.name)].map((name, index) => {
          const item = document.createElement('li');
          item.textContent = name;
          if (index >= before) item.classList.add('is-dropped');
          return item;
        }));
        box.querySelectorAll('[data-pile-tier]').forEach((tier) => tier.classList.toggle('is-on', count >= Number(tier.dataset.pileTier)));
        box.querySelector('[data-pile-deal]').textContent =
          count >= this.discountCount ? 'Your stack gets ' + this.discountPercent + '% off + free shipping.' : count >= this.freeCount ? 'Free shipping! One more for ' + this.discountPercent + '% off.' : 'Add one more piece and shipping is free.';
      }

      // Look W: the popup's work order lists the order and stamps the deal it reached.
      updateOrder(ticked) {
        const order = this.upsell.querySelector('[data-sheet-order]');
        if (!order) return;
        const count = ticked.length + 1;
        const tiers = (order.dataset.tiers || '').split('|');
        const level = count >= this.discountCount ? 2 : count >= this.freeCount ? 1 : 0;
        order.querySelector('[data-order-tier]').textContent = tiers[level] || '';
        const list = order.querySelector('[data-order-lines]');
        list.replaceChildren();
        const row = (name, price) => {
          const item = document.createElement('li');
          const label = document.createElement('span');
          const value = document.createElement('b');
          label.textContent = name;
          value.textContent = price;
          item.append(label, value);
          list.append(item);
        };
        row(this.addedName || 'Your item', 'In your cart');
        ticked.forEach((group) => row(group.name, this.money(group.price())));
        order.querySelector('[data-order-deal]').textContent =
          level === 2 ? this.discountPercent + '% off + free shipping' : level === 1 ? 'Free shipping. One more for ' + this.discountPercent + '% off.' : 'Add one more piece and shipping is free.';
        const stamp = order.querySelector('[data-order-stamp]');
        const text = level === 2 ? this.discountPercent + '% off' : level === 1 ? 'Free ship' : '';
        stamp.hidden = !text;
        if (text && stamp.textContent !== text) {
          stamp.textContent = text;
          // Restart the stamp animation each time the deal goes up a level.
          stamp.classList.remove('is-stamped');
          void stamp.offsetWidth;
          stamp.classList.add('is-stamped');
        }
        if (!text) stamp.textContent = '';
      }

      // Look T: a speed button in the offer box shows what that many items get.
      pickSpeed(button) {
        const box = button.closest('.om-v8l__speed');
        box.querySelectorAll('[data-speed]').forEach((other) => other.setAttribute('aria-pressed', String(other === button)));
        box.querySelector('[data-turntable]').dataset.rpm = button.dataset.speed;
        box.querySelector('[data-speed-rpm]').textContent = button.dataset.rpmText;
        box.querySelector('[data-speed-line]').textContent = button.dataset.line;
        box.querySelector('[data-speed-note]').textContent = button.dataset.note;
      }

      // Look T: the popup's record turns at 33⅓, 45 or 78 rpm for 1, 2 or 3+ items.
      updateSpin(count) {
        const spin = this.upsell.querySelector('[data-sheet-spin]');
        if (!spin) return;
        let rpm = '33';
        let title = '33⅓ rpm';
        let line = 'One item in your cart';
        let next = 'Add one more and shipping is free.';
        if (count >= this.discountCount) {
          rpm = '78';
          title = '78 rpm';
          line = this.discountPercent + '% off + free shipping';
          next = 'Full speed. The best deal.';
        } else if (count >= this.freeCount) {
          rpm = '45';
          title = '45 rpm';
          line = 'Free shipping';
          next = 'One more for ' + this.discountPercent + '% off.';
        }
        spin.querySelector('[data-turntable]').dataset.rpm = rpm;
        spin.querySelector('[data-spin-rpm]').textContent = title;
        spin.querySelector('[data-spin-line]').textContent = line;
        spin.querySelector('[data-spin-next]').textContent = next;
      }

      // Look I: the popup's fuel gauge fills one step per item, the needle follows.
      updateTank(count) {
        const tank = this.upsell.querySelector('[data-tank]');
        if (!tank) return;
        const gauge = tank.querySelector('[data-gauge]');
        const steps = Number(gauge.dataset.steps) || this.discountCount;
        const filled = Math.min(count, steps);
        const angle = Math.PI * (1 - filled / steps);
        const point = (radius) => [(100 + radius * Math.cos(angle)).toFixed(1), (100 - radius * Math.sin(angle)).toFixed(1)];
        const [x, y] = point(80);
        const [nx, ny] = point(64);
        gauge.querySelector('[data-gauge-arc]').setAttribute('d', 'M20 100 A80 80 0 0 1 ' + x + ' ' + y);
        const needle = gauge.querySelector('[data-gauge-needle]');
        needle.setAttribute('x2', nx);
        needle.setAttribute('y2', ny);
        gauge.setAttribute('aria-label', 'Fuel gauge: ' + filled + ' of ' + steps + ' items');
        const left = this.discountCount - count;
        let title;
        let line;
        if (count >= this.discountCount) {
          title = 'Tank full: ' + count + ' items';
          line = this.discountPercent + '% off + free shipping';
        } else if (count >= this.freeCount) {
          title = count + ' items: free shipping';
          line = 'Tick ' + (left === 1 ? 'one more' : left + ' more') + ' for ' + this.discountPercent + '% off.';
        } else {
          title = count + (count === 1 ? ' item' : ' items') + ' in the tank';
          line = 'Tick one more for free shipping.';
        }
        tank.querySelector('[data-tank-title]').textContent = title;
        tank.querySelector('[data-tank-line]').textContent = line;
      }

      // "More from the collection": a random, even mix of the configured product
      // types per visit. The cards printed by the server stay if anything fails.
      async mixCrossSell() {
        const section = this.querySelector('[data-cross]');
        if (!section) return;
        const limit = Number(section.dataset.limit);
        const exclude = section.dataset.exclude.split(',');
        const excludeTitle = (section.dataset.excludeTitle || '').trim();
        const types = section.dataset.types.split(',').map((type) => type.trim()).filter(Boolean);
        const shuffle = (list) => {
          for (let i = list.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [list[i], list[j]] = [list[j], list[i]];
          }
          return list;
        };
        try {
          const response = await fetch(section.dataset.collectionUrl + '/products.json?limit=250');
          if (!response.ok) return;
          const eligible = (await response.json()).products.filter(
            (product) =>
              !exclude.includes(product.handle) &&
              !(excludeTitle && product.title.toLowerCase().includes(excludeTitle)) &&
              product.images.length && product.variants.some((variant) => variant.available)
          );
          const perType = types.length ? Math.ceil(limit / types.length) : limit;
          const picked = types.length
            ? types.flatMap((type) => shuffle(eligible.filter((product) => product.product_type === type)).slice(0, perType))
            : shuffle(eligible);
          const cards = shuffle(picked).slice(0, limit);
          if (cards.length < limit) return;
          const grid = section.querySelector('[data-cross-grid]');
          grid.replaceChildren(
            ...cards.map((product) => {
              const card = document.createElement('a');
              card.className = 'om-v8l__cross-card';
              card.href = section.dataset.productUrl + product.handle;
              const image = document.createElement('img');
              const source = product.images[0].src;
              image.src = source + (source.includes('?') ? '&' : '?') + 'width=700';
              image.width = 450;
              image.height = 450;
              image.loading = 'lazy';
              image.alt = '';
              const title = document.createElement('strong');
              title.textContent = product.title;
              card.append(image, title);
              return card;
            })
          );
        } catch (error) {
          console.warn('More from the collection: keeping the default cards', error);
        }
      }

      // Top photos: a scroll-snap strip. Swipe it, or use the thumbnails laid over
      // the photo and the arrows at the ends of that row; the current thumbnail is marked.
      setUpHero() {
        const hero = this.querySelector('[data-hero]');
        const track = hero?.querySelector('[data-hero-track]');
        const thumbs = [...(hero?.querySelectorAll('[data-hero-thumb]') || [])];
        if (!track || !thumbs.length) return;
        const strip = hero.querySelector('[data-hero-thumbs]');
        // Look G prints a caption under the photo, one line per photo.
        const caption = this.querySelector('[data-hero-caption]');
        const captions = caption ? JSON.parse(caption.dataset.captions || '[]') : [];
        const count = this.querySelector('[data-hero-count]');
        const last = thumbs.length - 1;
        let index = 0;
        const mark = () => {
          thumbs.forEach((thumb, i) => (i === index ? thumb.setAttribute('aria-current', 'true') : thumb.removeAttribute('aria-current')));
          if (caption) caption.textContent = (captions[index] || '').trim();
          if (count) count.textContent = index + 1 + ' / ' + thumbs.length;
          // Keep the current thumbnail in the clear middle of its strip: the strip's
          // side padding is the faded zone where the arrows sit.
          const thumb = thumbs[index];
          const inset = parseFloat(getComputedStyle(strip).paddingLeft) || 0;
          const over = thumb.offsetLeft + thumb.offsetWidth - (strip.scrollLeft + strip.clientWidth - inset);
          const under = strip.scrollLeft + inset - thumb.offsetLeft;
          if (over > 0) strip.scrollLeft += over;
          else if (under > 0) strip.scrollLeft -= under;
        };
        const calm = window.matchMedia('(prefers-reduced-motion: reduce)');
        let glide = 0;
        // Slide the track to a photo with a slow ease (no snapping mid-way), so the move reads as one
        // calm glide rather than a jump. Visitors who ask for less motion get the jump.
        const glideTo = (target, duration) => {
          cancelAnimationFrame(glide);
          const from = track.scrollLeft;
          const to = target * track.clientWidth;
          if (calm.matches || from === to) {
            track.scrollLeft = to;
            return;
          }
          const start = performance.now();
          track.style.scrollSnapType = 'none';
          const step = (now) => {
            const t = Math.min(1, (now - start) / duration);
            const eased = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
            track.scrollLeft = from + (to - from) * eased;
            if (t < 1) glide = requestAnimationFrame(step);
            else track.style.scrollSnapType = '';
          };
          glide = requestAnimationFrame(step);
        };
        const show = (next, duration = 650) => {
          index = Math.max(0, Math.min(last, next));
          glideTo(index, duration);
          mark();
        };
        track.addEventListener(
          'scroll',
          () => {
            const current = Math.round(track.scrollLeft / track.clientWidth);
            if (current === index) return;
            index = current;
            mark();
          },
          { passive: true }
        );

        // Autoplay: the next photo every 5 seconds. Any touch, swipe, wheel, key, arrow or thumbnail
        // pauses it; it starts again after 5 quiet seconds. It also waits while the zoom viewer is
        // open, the gallery is off screen, the tab is hidden or a mouse rests on the photo, and it
        // never runs for visitors who ask for less motion.
        const DELAY = 5000;
        let timer = 0;
        let visible = false;
        let hovering = false;
        let holding = false;
        const viewer = this.querySelector('dialog[data-viewer="hero"]');
        const advance = () => {
          if (index === last) {
            // Back to the first photo with a soft fade instead of racing past every photo.
            track.style.transition = 'opacity .45s ease';
            track.style.opacity = '0';
            setTimeout(() => {
              cancelAnimationFrame(glide);
              index = 0;
              track.scrollLeft = 0;
              mark();
              track.style.opacity = '1';
              setTimeout(() => (track.style.transition = ''), 500);
            }, 450);
          } else {
            show(index + 1, 900);
          }
        };
        const schedule = () => {
          clearTimeout(timer);
          if (last < 1 || calm.matches) return;
          timer = setTimeout(() => {
            if (visible && !hovering && !holding && !document.hidden && !viewer?.open) advance();
            schedule();
          }, DELAY);
        };
        const userMoved = () => {
          cancelAnimationFrame(glide);
          track.style.scrollSnapType = '';
          schedule();
        };
        ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach((type) => hero.addEventListener(type, userMoved, { passive: true }));
        // Only a real mouse counts as resting on the photo: a tap on a phone also fires mouseenter,
        // and its mouseleave never comes, which would stop the slideshow for good.
        hero.addEventListener('pointerenter', (event) => (hovering = event.pointerType === 'mouse'));
        hero.addEventListener('pointerleave', (event) => {
          if (event.pointerType !== 'mouse') return;
          hovering = false;
          schedule();
        });
        // A finger kept on the photo holds it; the 5 quiet seconds start when it lifts.
        hero.addEventListener('touchstart', () => (holding = true), { passive: true });
        ['touchend', 'touchcancel'].forEach((type) =>
          hero.addEventListener(type, () => {
            holding = false;
            schedule();
          }, { passive: true })
        );
        new IntersectionObserver((entries) => (visible = entries[0].isIntersecting), { threshold: 0.5 }).observe(hero);
        schedule();

        hero.querySelector('[data-hero-prev]').addEventListener('click', () => show(index === 0 ? last : index - 1));
        hero.querySelector('[data-hero-next]').addEventListener('click', () => show(index === last ? 0 : index + 1));
        thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => show(i)));
        track.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') show(index - 1);
          if (event.key === 'ArrowRight') show(index + 1);
        });
      }

      groupsIn(root) {
        return [...root.querySelectorAll('[data-group]')]
          .filter((element) => this.products[element.dataset.item])
          .map((element) => new OmV8Group(element, this.products));
      }

      money(cents) {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'USD',
        }).format(cents / 100);
      }

      // "cap", "cap & tee", "cap, tee & jacket"
      label(groups) {
        const names = groups.map((group) => group.name.toLowerCase());
        if (names.length < 3) return names.join(' & ');
        return names.slice(0, -1).join(', ') + ' & ' + names[names.length - 1];
      }

      // What an order of these groups costs with the store offers applied.
      quote(groups) {
        const sum = groups.reduce((total, group) => total + group.price(), 0);
        const discounted = groups.length >= this.discountCount;
        return {
          count: groups.length,
          sum,
          total: discounted ? Math.round(sum * (1 - this.discountPercent / 100)) : sum,
          discounted,
          free: groups.length >= this.freeCount,
        };
      }

      perk(count) {
        if (count >= this.discountCount) return this.discountPercent + '% off + free shipping';
        if (count >= this.freeCount) return 'Free shipping';
        return '';
      }

      // The groups the visitor is buying right now.
      active() {
        if (this.look === 'a') return this.groups.filter((group) => group.element.querySelector('[data-toggle]').checked);
        if (this.look === 'b') {
          const keys = this.form.querySelector('[data-set]:checked').value.split(',');
          return this.groups.filter((group) => keys.includes(group.key));
        }
        const key = this.form.querySelector('[data-tab]:checked').value;
        return this.groups.filter((group) => group.key === key);
      }

      update() {
        const money = (cents) => this.money(cents);
        const active = this.active();
        const quote = this.quote(active);

        this.groups.forEach((group) => {
          group.refresh(money);
          group.element.classList.toggle('is-off', !active.includes(group));
          // The item's tab shows the color picked for it.
          const tabPhoto = this.form.querySelector('[data-tab-thumb="' + group.key + '"]');
          const image = (group.variant() || group.matching()[0])?.image;
          if (tabPhoto && image && !tabPhoto.src.endsWith(image)) tabPhoto.src = image;
        });

        if (this.look !== 'c') {
          this.form.querySelector('[data-total]').textContent = quote.count ? 'Total ' + money(quote.total) : '';
          this.form.querySelector('[data-was]').textContent = quote.discounted ? money(quote.sum) : '';
          this.form.querySelector('[data-ship]').textContent = quote.free ? 'Free shipping' : '';
        }
        this.button.textContent = quote.count ? 'Add ' + this.label(active) + ' to cart' : 'Tick an item first';
        this.button.disabled = !quote.count;

        if (this.look === 'a') this.updateLadder(active, quote.count);
        if (this.look === 'b') this.updateSets(money);
        this.updateBar(active[0], money);
      }

      // The bar shows only after the buy area has scrolled above the screen, and
      // hides again once the landing section ends, so it never covers the footer.
      setUpBar() {
        const bar = this.querySelector('[data-bar]');
        if (!bar) return;
        const place = () => {
          const buy = this.form.getBoundingClientRect();
          const box = this.getBoundingClientRect();
          const show = buy.bottom < 0 && box.bottom > window.innerHeight;
          bar.classList.toggle('is-shown', show);
          bar.toggleAttribute('inert', !show);
          // Lift the theme's floating back-to-top button above the bar.
          document.body.classList.toggle('om-v8l-bar-visible', show);
          if (show) {
            bar.style.left = box.left + 'px';
            bar.style.right = window.innerWidth - box.right + 'px';
          }
        };
        window.addEventListener('scroll', place, { passive: true });
        window.addEventListener('resize', place);
        place();
      }

      // Sticky bar at the bottom: the item being chosen, its price, a button back to the buy area.
      updateBar(group, money) {
        const bar = this.querySelector('[data-bar]');
        if (!bar || !group) return;
        bar.querySelector('[data-bar-name]').textContent = group.name;
        bar.querySelector('[data-bar-price]').textContent = money(group.price());
        const photo = this.form.querySelector('[data-tab-thumb="' + group.key + '"]');
        const image = bar.querySelector('[data-bar-img]');
        if (photo && image.src !== photo.src) image.src = photo.src;
      }

      updateLadder(active, count) {
        // What ticking one more item would unlock, shown on the rows that are not ticked.
        const next = count + 1;
        const hint =
          next === this.discountCount
            ? 'Add it for ' + this.discountPercent + '% off everything'
            : next === this.freeCount
              ? 'Add it and shipping is free'
              : '';
        this.groups.forEach((group) => {
          group.element.querySelector('[data-hint]').textContent = count && !active.includes(group) ? hint : '';
        });

        const steps = [...this.form.querySelectorAll('[data-step]')];
        steps.forEach((step, index) => {
          const from = Number(step.dataset.step);
          const to = steps[index + 1] ? Number(steps[index + 1].dataset.step) : Infinity;
          step.classList.toggle('is-current', count >= from && count < to);
        });

        let nudge = 'Tick an item to start.';
        if (count >= this.discountCount) nudge = 'Best deal: ' + this.discountPercent + '% off + free shipping.';
        else if (count >= this.freeCount) nudge = 'Shipping is free. Add ' + (this.discountCount - count) + ' more for ' + this.discountPercent + '% off.';
        else if (count) nudge = 'Add ' + (this.freeCount - count) + ' more item and shipping is free.';
        this.form.querySelector('[data-nudge]').textContent = nudge;
      }

      updateSets(money) {
        this.form.querySelectorAll('[data-set]').forEach((input) => {
          const card = input.closest('label');
          const keys = input.value.split(',');
          const groups = this.groups.filter((group) => keys.includes(group.key));
          card.hidden = groups.length !== keys.length;
          const quote = this.quote(groups);
          card.querySelector('[data-set-price]').textContent = money(quote.total);
          card.querySelector('[data-set-was]').textContent = quote.discounted ? money(quote.sum) : '';
          card.querySelector('[data-set-badge]').textContent = this.perk(quote.count);
        });
      }

      // Cart lines for the groups, or null after pointing at the first missing choice.
      lines(groups, status) {
        const lines = [];
        for (const group of groups) {
          const variant = group.variant();
          if (!variant || !variant.available) {
            status.textContent = 'Please choose your ' + group.name.toLowerCase() + ' size.';
            group.element.querySelector('select')?.focus();
            return null;
          }
          lines.push({ id: variant.id, quantity: 1 });
        }
        return lines;
      }

      // Adds the lines; returns the cart response (with the drawer's sections) or null.
      async post(lines, button, status) {
        const drawer = document.querySelector('cart-drawer');
        if (!drawer?.renderContents) {
          status.textContent = 'Please refresh the page to load the cart.';
          return null;
        }
        this.busy = true;
        button.disabled = true;
        const label = button.textContent;
        button.textContent = 'Adding…';
        try {
          drawer.setActiveElement(this.button);
          const sections = drawer.getSectionsToRender().map((section) => section.id);
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify({ items: lines, sections, sections_url: location.pathname }),
          });
          // A busy store can answer with an HTML error page instead of JSON; never show that raw.
          const data = await response.json().catch(() => null);
          if (!data) throw Error('');
          if (!response.ok || data.status) throw Error(data.description || '');
          return data;
        } catch (error) {
          // Shopify's own reason (e.g. sold out) is worth showing; a dropped connection (TypeError) is not.
          const reason = error instanceof TypeError ? '' : error.message;
          status.textContent = reason
            ? reason + ' Please check your cart before trying again.'
            : 'We could not add it just now. Please wait a moment and tap the button again.';
          return null;
        } finally {
          this.busy = false;
          button.textContent = label;
          button.disabled = false;
        }
      }

      openCart(data) {
        const drawer = document.querySelector('cart-drawer');
        drawer.renderContents(data);
        drawer.classList.remove('is-empty');
      }

      async submit() {
        if (this.busy) return;
        const active = this.active();
        const lines = this.lines(active, this.status);
        if (!lines || !lines.length) return;
        const data = await this.post(lines, this.button, this.status);
        if (!data) return;
        // Look C offers the matching items once per page view, then opens the cart.
        if (this.upsell && !this.upsellShown && this.openUpsell(active[0], data)) return;
        this.openCart(data);
      }

      setUpUpsell() {
        this.upsellForm = this.upsell.querySelector('[data-upsell-form]');
        this.upsellButton = this.upsell.querySelector('[data-upsell-submit]');
        this.upsellStatus = this.upsell.querySelector('[data-upsell-status]');
        this.upsellGroups = this.groupsIn(this.upsell);

        this.upsellForm.addEventListener('change', () => {
          this.upsellStatus.textContent = '';
          this.updateUpsell();
        });
        this.upsellForm.addEventListener('submit', async (event) => {
          event.preventDefault();
          if (this.busy) return;
          const lines = this.lines(this.upsellTicked(), this.upsellStatus);
          if (!lines || !lines.length) return;
          const data = await this.post(lines, this.upsellButton, this.upsellStatus);
          if (data) this.closeUpsell(data);
        });
        // Esc and a tap on the backdrop both mean "no thanks".
        this.upsell.addEventListener('cancel', (event) => {
          event.preventDefault();
          if (!this.busy) this.closeUpsell();
        });
        this.upsell.addEventListener('click', (event) => {
          if (event.target === this.upsell && !this.busy) this.closeUpsell();
        });
      }

      // One photo viewer per product ("See photos"): a native <dialog> that also
      // opens on top of the popup; Esc and Close dismiss it.
      setUpViewers() {
        this.viewers = {};
        this.querySelectorAll('[data-viewer]').forEach((dialog) => {
          const track = dialog.querySelector('[data-viewer-track]');
          const slides = [...dialog.querySelectorAll('[data-viewer-slide]')];
          if (!track || !slides.length) return;
          const count = dialog.querySelector('[data-viewer-count]');
          let index = 0;
          const label = () => (count.textContent = 'Photo ' + (index + 1) + ' of ' + slides.length);
          // The track is a scroll-snap strip: a swipe moves it, the arrows scroll it for you.
          const show = (next) => {
            index = (next + slides.length) % slides.length;
            track.scrollLeft = slides[index].offsetLeft - track.offsetLeft;
            label();
          };
          // After a swipe the slide nearest the left edge is the current one.
          track.addEventListener(
            'scroll',
            () => {
              const left = track.scrollLeft + track.offsetLeft;
              index = slides.reduce((best, slide, i) => (Math.abs(slide.offsetLeft - left) < Math.abs(slides[best].offsetLeft - left) ? i : best), 0);
              label();
            },
            { passive: true }
          );
          dialog.querySelectorAll('.om-viewer__nav').forEach((button) => (button.hidden = slides.length < 2));
          dialog.querySelector('[data-viewer-prev]').addEventListener('click', () => show(index - 1));
          dialog.querySelector('[data-viewer-next]').addEventListener('click', () => show(index + 1));
          dialog.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowLeft') show(index - 1);
            if (event.key === 'ArrowRight') show(index + 1);
          });
          // A tap on the dark area around the panel closes it too.
          dialog.addEventListener('click', (event) => {
            if (event.target === dialog) dialog.close();
          });
          this.viewers[dialog.dataset.viewer] = { dialog, slides, show };
        });
      }

      // A tap on a top photo (or its Zoom button) opens that photo in the hero viewer.
      // There a tap zooms in on the spot tapped, a drag moves around, another tap zooms out.
      setUpHeroZoom() {
        const track = this.querySelector('[data-hero-track]');
        const viewer = this.viewers.hero;
        if (!track || !viewer) return;
        const open = () => {
          // The photos wait (lazy) until someone opens the viewer; then load them all.
          viewer.dialog.querySelectorAll('img[loading="lazy"]').forEach((image) => (image.loading = 'eager'));
          viewer.dialog.showModal();
          viewer.show(Math.round(track.scrollLeft / track.clientWidth));
        };
        track.addEventListener('click', (event) => {
          if (event.target.closest('img')) open();
        });
        this.querySelector('[data-hero-zoom]')?.addEventListener('click', open);

        const viewerTrack = viewer.dialog.querySelector('[data-viewer-track]');
        const reset = () =>
          viewer.dialog.querySelectorAll('[data-zoom].is-zoomed').forEach((frame) => {
            frame.classList.remove('is-zoomed');
            frame.style.removeProperty('--zoom-x');
            frame.style.removeProperty('--zoom-y');
          });
        // Zoomed in, the strip must not swipe to the next photo.
        const sync = () => viewerTrack.classList.toggle('is-locked', Boolean(viewer.dialog.querySelector('[data-zoom].is-zoomed')));
        viewer.dialog.addEventListener('close', () => {
          reset();
          sync();
        });
        viewer.dialog.querySelectorAll('[data-viewer-prev], [data-viewer-next]').forEach((button) =>
          button.addEventListener('click', () => {
            reset();
            sync();
          }, { capture: true })
        );
        viewer.dialog.querySelectorAll('[data-zoom]').forEach((frame) => {
          const clamp = (value) => Math.max(0, Math.min(100, value));
          const point = (event) => {
            const box = frame.getBoundingClientRect();
            return { x: ((event.clientX - box.left) / box.width) * 100, y: ((event.clientY - box.top) / box.height) * 100 };
          };
          let drag = null;
          frame.addEventListener('pointerdown', (event) => {
            if (!frame.classList.contains('is-zoomed')) return;
            const style = getComputedStyle(frame);
            drag = { x: event.clientX, y: event.clientY, zx: parseFloat(style.getPropertyValue('--zoom-x')), zy: parseFloat(style.getPropertyValue('--zoom-y')), moved: false };
            frame.setPointerCapture(event.pointerId);
          });
          frame.addEventListener('pointermove', (event) => {
            if (!drag) return;
            const box = frame.getBoundingClientRect();
            const dx = event.clientX - drag.x;
            const dy = event.clientY - drag.y;
            if (Math.abs(dx) + Math.abs(dy) > 6) drag.moved = true;
            // Dragging right shows more of the left side, like moving the photo by hand.
            frame.style.setProperty('--zoom-x', clamp(drag.zx - (dx / box.width) * 100) + '%');
            frame.style.setProperty('--zoom-y', clamp(drag.zy - (dy / box.height) * 100) + '%');
          });
          frame.addEventListener('pointerup', (event) => {
            const wasDrag = drag?.moved;
            drag = null;
            if (wasDrag) return;
            if (frame.classList.contains('is-zoomed')) {
              reset();
            } else {
              const spot = point(event);
              frame.style.setProperty('--zoom-x', clamp(spot.x) + '%');
              frame.style.setProperty('--zoom-y', clamp(spot.y) + '%');
              frame.classList.add('is-zoomed');
            }
            sync();
          });
          frame.addEventListener('pointercancel', () => (drag = null));
        });
      }

      // Opens the viewer of the group's product on the photo of the chosen color.
      openViewer(group) {
        const viewer = this.viewers[group.key];
        if (!viewer) return;
        const fileOf = (url) => (url || '').split('?')[0].split('/').pop();
        const current = fileOf((group.variant() || group.matching()[0])?.image);
        // "Add to my order" only makes sense from a popup row that is not ticked yet.
        const toggle = this.upsell?.contains(group.element) ? group.element.querySelector('[data-toggle]') : null;
        const add = viewer.dialog.querySelector('[data-viewer-add]');
        add.hidden = !toggle || toggle.checked;
        add.onclick = () => {
          viewer.dialog.close();
          toggle.checked = true;
          toggle.dispatchEvent(new Event('change', { bubbles: true }));
          group.element.scrollIntoView({ block: 'nearest' });
        };
        viewer.dialog.showModal();
        viewer.show(Math.max(0, viewer.slides.findIndex((slide) => fileOf(slide.querySelector('img').getAttribute('src')) === current)));
      }

      upsellTicked() {
        return this.upsellGroups.filter((group) => !group.element.hidden && group.element.querySelector('[data-toggle]').checked);
      }

      openUpsell(added, data) {
        this.upsellGroups.forEach((group) => {
          group.element.hidden = group.key === added.key;
          group.element.querySelector('[data-toggle]').checked = !group.element.hidden && (this.preticked || []).includes(group.key);
        });
        if (this.upsellGroups.every((group) => group.element.hidden)) return false;
        this.upsellShown = true;
        this.pendingCart = data;
        this.addedName = added.name;
        this.upsell.querySelector('[data-upsell-title]').textContent = added.name + ' added to your cart';
        this.upsellStatus.textContent = '';
        this.updateUpsell();
        this.upsell.showModal();
        return true;
      }

      updateUpsell() {
        const ticked = this.upsellTicked();
        this.upsellGroups.forEach((group) => {
          group.refresh((cents) => this.money(cents));
          group.element.classList.toggle('is-off', !ticked.includes(group));
        });
        const perk = this.perk(ticked.length + 1);
        this.upsell.querySelector('[data-upsell-deal]').textContent = ticked.length && perk ? 'You get: ' + perk.toLowerCase() : '';
        this.upsellButton.textContent = ticked.length ? 'Add ' + this.label(ticked) : 'Tick an item to add it';
        this.upsellButton.disabled = !ticked.length;
        this.updateTank(ticked.length + 1);
        this.updateSpin(ticked.length + 1);
        this.updateOrder(ticked);
        this.updatePile(ticked);
        this.updateSpeedo(ticked.length + 1);
        this.updatePour(ticked.length + 1);
        this.updateVolume(ticked.length + 1);
        this.updateRack(ticked.length + 1);
        this.updateNeedle(ticked.length + 1);
        // Looks L and O: each price sticker or tag lights up once the ticked items reach it.
        this.upsell.querySelectorAll('[data-sticker]').forEach((sticker) => {
          sticker.classList.toggle('is-on', ticked.length + 1 >= Number(sticker.dataset.sticker));
        });
      }

      closeUpsell(data) {
        this.upsell.close();
        this.openCart(data || this.pendingCart);
      }

      onClick(event) {
        const skip = event.target.closest('[data-upsell-skip]');
        if (skip && !this.busy) return this.closeUpsell();

        const view = event.target.closest('[data-view]');
        if (view) {
          const element = view.closest('[data-group]');
          const group = [...this.groups, ...(this.upsellGroups || []), ...(this.quickGroups || [])].find((entry) => entry.element === element);
          if (group) this.openViewer(group);
          return;
        }

        const guide = event.target.closest('[data-size-guide]');
        if (guide) return this.querySelector('[data-size-dialog="' + guide.dataset.sizeGuide + '"]')?.showModal();

        const close = event.target.closest('[data-close]');
        if (close) return close.closest('dialog').close();

        const speed = event.target.closest('[data-speed]');
        if (speed) return this.pickSpeed(speed);

        // With Quick add, "Choose" buttons and the sticky bar open the piece's own popup.
        const current = event.target.closest('[data-choose-current]');
        if (current && this.quick) return this.openQuick(this.active()[0]?.key);

        // "Choose" buttons further down the page pick that item and go back to the buy area.
        const choose = event.target.closest('[data-choose]');
        if (choose && this.quick && this.openQuick(choose.dataset.choose, choose.dataset.chooseColor)) return;
        if (choose) {
          const tab = this.form.querySelector('[data-tab][value="' + choose.dataset.choose + '"]');
          if (tab) {
            tab.checked = true;
            tab.dispatchEvent(new Event('change', { bubbles: true }));
          }
          this.form.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }

        const scroll = event.target.closest('[data-scroll-to]');
        if (scroll) document.getElementById(scroll.dataset.scrollTo)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  );
}
